import { css } from 'styled-components';

/**
 * Shared `<details>` disclosure styles, consumed by all accordion components.
 *
 * Motion uses `var(--global-motion)`, so the site-wide `prefers-reduced-motion`
 * switch in `global-styles.ts` already covers it — don’t add another gate here.
 */
export const disclosureStyles = css`
  interpolate-size: allow-keywords;

  summary {
    cursor: pointer;

    /* Hide the default disclosure marker on Chrome and Firefox */
    list-style-type: none;

    /* Hide the default disclosure marker on Safari */
    &::-webkit-details-marker {
      display: none;
    }
  }

  &::details-content {
    height: 0;
    overflow: clip;
    transition:
      height var(--global-motion),
      content-visibility var(--global-motion) allow-discrete;
  }

  &[open]::details-content {
    height: auto;
  }
`;

/**
 * The colour contract an accordion card reads.
 *
 * `AccordionList` publishes one of these sets on the `<ul>` and the values
 * inherit to every card inside it, so nothing has to walk or rewrite children.
 * An `Accordion` given an explicit `variant` applies the set to its own `<li>`,
 * which sits closer to the card and therefore wins over the inherited one.
 *
 * Cards are deliberately not the colours of the surrounding `Section`: a card
 * stays legible on any `colorScheme`, so these do not derive from `--global-*`.
 *
 * Each set also restates `--global-link-color` and friends. Prose links inside
 * a description are bare `<a>`s, which `global-styles.ts` colours from those
 * vars — set by the `Section`, against a background the card has just replaced.
 * A card owns its own background, so it has to own its own link colours too.
 */
export const accordionSchemeLight = css`
  --accordion-bg: ${({ theme }) => theme.colors.white};
  --accordion-fg: ${({ theme }) => theme.colors.blue800};
  --accordion-hover-bg: ${({ theme }) => theme.colors.blue100};
  --accordion-icon: ${({ theme }) => theme.colors.blue500};
  --accordion-link: ${({ theme }) => theme.colors.blue500};
  --accordion-border: ${({ theme }) => theme.colors.grey100};

  --global-link-color: ${({ theme }) => theme.colors.blue500};
  --global-hover-color: ${({ theme }) => theme.colors.blue700};
  --global-visited-color: ${({ theme }) => theme.colors.purple700};
`;

export const accordionSchemeDark = css`
  --accordion-bg: ${({ theme }) => theme.colors.blue700};
  --accordion-fg: ${({ theme }) => theme.colors.white};
  --accordion-hover-bg: ${({ theme }) => theme.colors.blue600};
  --accordion-icon: ${({ theme }) => theme.colors.white};
  --accordion-link: ${({ theme }) => theme.colors.blue300};
  --accordion-border: ${({ theme }) => theme.colors.blue600};

  --global-link-color: ${({ theme }) => theme.colors.blue300};
  --global-hover-color: ${({ theme }) => theme.colors.white};
  --global-visited-color: ${({ theme }) => theme.colors.purple200};
`;
